const THUMB_COUNT = 3

export default function InitImageGallery() {
    // the main+thumbnail swap is a purely visual frontend enhancement — it
    // never runs in the backend, where editors need to see every image
    // plainly (see the ".neos-backend" override in ImageGallery.scss)
    if (window.neos || document.body.classList.contains('neos-backend')) {
        return
    }

    document.querySelectorAll('[data-image-gallery-list]').forEach((listEl) => {
        const items = Array.from(listEl.querySelectorAll('[data-image-gallery-item]'))

        if (!items.length) {
            return
        }

        const root = listEl.closest('[data-image-gallery]')
        const prevButton = root.querySelector('[data-image-gallery-prev]')
        const nextButton = root.querySelector('[data-image-gallery-next]')

        let activeIndex = 0

        // small crossfade on src change — nothing fancy, just avoids the
        // jarring instant pop when an image swaps between its full-size and
        // thumbnail version (see the opacity transition in ImageGallery.scss).
        // Deliberately not gated on the image's "load" event: a src that's
        // already in the browser cache (very common here — thumb/full
        // versions of images already shown) fires "load" almost instantly,
        // often before the browser has even painted the opacity:0 frame, so
        // the fade never became visible. A double requestAnimationFrame
        // guarantees a real frame gets painted at opacity:0 before we
        // transition back to 1, regardless of load timing.
        function setImageSrc(item, useFullSrc) {
            const img = item.querySelector('.image-gallery__item-image')
            if (!img) {
                return
            }
            const nextSrc = useFullSrc ? img.dataset.fullSrc : img.dataset.thumbSrc
            if (img.getAttribute('src') === nextSrc) {
                return
            }
            img.style.opacity = '0'
            img.src = nextSrc
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    img.style.opacity = '1'
                })
            })
        }

        function render() {
            const thumbCount = Math.min(THUMB_COUNT, items.length - 1)

            items.forEach((item, index) => {
                item.classList.remove('is-main', 'is-thumb', 'is-visible')
                // visual order must follow each item's offset from the
                // active image, not its original position in the
                // collection — flexbox otherwise lays thumbs out by DOM
                // order, so after wrapping around, whichever wrapped item
                // has the lowest source index always lands in the first
                // thumbnail slot instead of cycling through normally
                item.style.order = ''

                if (index === activeIndex) {
                    item.classList.add('is-main')
                    setImageSrc(item, true)
                    // ".is-visible" is what actually fades/scales the main
                    // image in (see ImageGallery.scss) — added a couple of
                    // frames later so the browser paints the ".is-main"
                    // (opacity:0) state first; adding both classes in the
                    // same tick would give the transition nothing to
                    // animate from and it'd just snap straight to visible
                    requestAnimationFrame(() => {
                        requestAnimationFrame(() => {
                            item.classList.add('is-visible')
                        })
                    })
                    return
                }

                const offset = (index - activeIndex + items.length) % items.length
                if (offset >= 1 && offset <= thumbCount) {
                    item.classList.add('is-thumb')
                    item.style.order = String(offset)
                    setImageSrc(item, false)
                }
            })

            const hasMultiple = items.length > 1
            if (prevButton) {
                prevButton.style.display = hasMultiple ? '' : 'none'
            }
            if (nextButton) {
                nextButton.style.display = hasMultiple ? '' : 'none'
            }
        }

        // delegated on the list itself — items are re-classed (not
        // recreated) on every render, so a single listener here avoids
        // re-attaching (and stacking) a handler per item on each render
        listEl.addEventListener('click', (event) => {
            const item = event.target.closest('[data-image-gallery-item]')
            if (!item || !item.classList.contains('is-thumb')) {
                return
            }
            activeIndex = items.indexOf(item)
            render()
        })

        if (prevButton) {
            prevButton.addEventListener('click', () => {
                activeIndex = (activeIndex - 1 + items.length) % items.length
                render()
            })
        }

        if (nextButton) {
            nextButton.addEventListener('click', () => {
                activeIndex = (activeIndex + 1) % items.length
                render()
            })
        }

        render()
    })
}
